<template>
  <div class="page-container">
    <el-card shadow="never">
      <div class="toolbar">
        <div class="toolbar-left">
          <el-input
            v-model="keyword"
            placeholder="搜索商品名称"
            prefix-icon="el-icon-search"
            clearable
            size="small"
            style="width: 220px; margin-right: 12px;"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch"
          />
          <el-select
            v-model="categoryId"
            placeholder="商品分类"
            clearable
            size="small"
            style="width: 150px; margin-right: 12px;"
            @change="handleSearch"
          >
            <el-option
              v-for="cat in categoryList"
              :key="cat.id"
              :label="cat.name"
              :value="cat.id"
            />
          </el-select>
          <el-select
            v-model="shopId"
            placeholder="所属店铺"
            clearable
            filterable
            size="small"
            style="width: 180px;"
            @change="handleSearch"
          >
            <el-option
              v-for="shop in shopList"
              :key="shop.id"
              :label="shop.name"
              :value="shop.id"
            />
          </el-select>
          <el-select
            v-model="statusFilter"
            placeholder="上下架"
            clearable
            size="small"
            style="width: 120px;"
            @change="handleSearch"
          >
            <el-option label="全部" :value="-1" />
            <el-option label="上架" :value="1" />
            <el-option label="下架" :value="0" />
          </el-select>
          <el-button type="primary" size="small" icon="el-icon-search" @click="handleSearch">
            搜索
          </el-button>
          <el-button size="small" icon="el-icon-refresh" @click="handleReset">
            重置
          </el-button>
        </div>

      </div>

      <el-table
        :data="tableData"
        v-loading="loading"
        border
        stripe
        style="width: 100%; margin-top: 16px;"
      >
        <el-table-column prop="id" label="ID" width="70" align="center" />
        <el-table-column label="商品图片" width="90" align="center">
          <template slot-scope="scope">
            <el-image
              v-if="getFirstImage(scope.row)"
              :src="getFirstImage(scope.row)"
              fit="cover"
              style="width: 56px; height: 56px; border-radius: 4px;"
            />
          </template>
        </el-table-column>
        <el-table-column prop="name" label="商品名称" min-width="140" show-overflow-tooltip />
        <el-table-column prop="shopName" label="所属店铺" min-width="120" show-overflow-tooltip />
        <el-table-column prop="categoryName" label="分类" width="100" align="center" />
        <el-table-column prop="itemValue" label="商品价值" width="110" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.itemValue }}
          </template>
        </el-table-column>
        <el-table-column prop="dailyRent" label="日租金" width="100" align="center">
          <template slot-scope="scope">
            ¥{{ scope.row.dailyRent }}
          </template>
        </el-table-column>
        <el-table-column prop="stock" label="库存" width="70" align="center" />
        <el-table-column prop="quantity" label="数量" width="70" align="center">
          <template slot-scope="scope">
            <span style="font-size:12px;color:#909399;">{{ scope.row.quantity || 1 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="上下架" width="80" align="center">
          <template slot-scope="scope">
            <el-switch
              :value="scope.row.status === 1"
              active-color="#67c23a"
              inactive-color="#dcdfe6"
              @change="handleToggleStatus(scope.row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="320" fixed="right" align="center" v-if="!isPlatformAdmin">
          <template slot-scope="scope">
            <el-button type="text" size="small" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" size="small" @click="handleEditPeriods(scope.row)">租期</el-button>
            <el-button type="text" size="small" @click="handleViewQr(scope.row)">商品码</el-button>
            <el-button type="text" size="small" style="color: #ff4d4f;" @click="handleDelete(scope.row)">
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrapper">
        <el-pagination
          background
          layout="total, prev, pager, next"
          :total="total"
          :page-size="pageSize"
          :current-page.sync="currentPage"
          @current-change="loadData"
        />
      </div>
    </el-card>

    <goods-form ref="goodsForm" @saved="loadData" />
    <rent-period-form ref="rentPeriodForm" @saved="loadData" />

    <el-dialog :title="'商品码 - ' + currentGoodsName" :visible.sync="showQrDialog" width="360px" align="center">
      <div v-loading="qrLoading" style="min-height: 260px;">
        <img v-if="qrCodeUrl" :src="qrCodeUrl" style="width: 260px; height: 260px;" alt="商品码" />
        <div style="color: #999; margin-top: 12px;">支付宝扫一扫，进入商品详情页</div>
      </div>
      <div slot="footer">
        <el-button @click="showQrDialog = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getGoodsList, deleteGoods, updateGoodsStatus, generateGoodsQrCode } from '@/api/goods'
import { getShopList } from '@/api/shop'
import { getAllCategories } from '@/api/category'
import GoodsForm from './GoodsForm.vue'
import RentPeriodForm from './RentPeriodForm.vue'

export default {
  name: 'GoodsList',
  components: { GoodsForm, RentPeriodForm },
  computed: {
    isPlatformAdmin() {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
      return userInfo.role === 'PLATFORM'
    }
  },
  data() {
    return {
      tableData: [],
      loading: false,
      keyword: '',
      categoryId: null,
      shopId: null,
      shopList: [],
      statusFilter: -1,
      categoryList: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showQrDialog: false,
      qrCodeUrl: '',
      qrLoading: false,
      currentGoodsName: ''
    }
  },
  created() {
    this.loadData()
    this.loadShops()
    this.loadCategories()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          size: this.pageSize,
          status: this.statusFilter != null ? this.statusFilter : -1
        }
        if (this.keyword) params.keyword = this.keyword
        if (this.categoryId) params.categoryId = this.categoryId
        if (this.shopId) params.shopId = this.shopId
        const res = await getGoodsList(params)
        if (res.data && res.data.records) {
          this.tableData = res.data.records
          this.total = res.data.total || 0
        }
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async loadShops() {
      try {
        const res = await getShopList({ page: 1, size: 999 })
        this.shopList = (res.data && res.data.records) || []
      } catch (e) {
        console.error(e)
      }
    },
    async loadCategories() {
      try {
        const res = await getAllCategories()
        this.categoryList = res.data || []
      } catch (e) {
        console.error(e)
      }
    },
    handleSearch() {
      this.currentPage = 1
      this.loadData()
    },
    handleReset() {
      this.keyword = ''
      this.categoryId = null
      this.shopId = null
      this.statusFilter = -1
      this.currentPage = 1
      this.loadData()
    },
    handleCreate() {
      this.$refs.goodsForm.open()
    },
    handleEdit(row) {
      this.$refs.goodsForm.open(row)
    },
    handleEditPeriods(row) {
      this.$refs.rentPeriodForm.open(row)
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除该商品吗？', '提示', { type: 'warning' })
        await deleteGoods(row.id)
        this.$message.success('删除成功')
        this.loadData()
      } catch (e) {
        if (e !== 'cancel') {
          console.error(e)
        }
      }
    },
    async handleToggleStatus(row) {
      const action = row.status === 1 ? '下架' : '上架'
      try {
        await this.$confirm(`确定要${action}该商品吗？`, '提示', { type: 'warning' })
        await updateGoodsStatus(row.id, row.status === 1 ? 0 : 1)
        this.$message.success(`${action}成功`)
        this.loadData()
      } catch (e) {
        if (e !== 'cancel') {
          console.error(e)
        }
      }
    },
    getFirstImage(row) {
      if (!row) return ''
      let images = row.images || row.coverImage || row.image
      if (!images) return ''
      try {
        const arr = JSON.parse(images)
        return Array.isArray(arr) && arr.length > 0 ? arr[0] : ''
      } catch {
        if (images.includes(',')) {
          return images.split(',')[0] || ''
        }
        return images
      }
    },
    async handleViewQr(row) {
      this.currentGoodsName = row.name || ''
      this.showQrDialog = true
      this.qrLoading = true
      this.qrCodeUrl = ''
      try {
        const res = await generateGoodsQrCode(row.id)
        const url = res && res.data && res.data.qrCodeUrl
        this.qrCodeUrl = url ? String(url).trim() : ''
      } catch (e) {
        this.$message.error(e.message || '生成商品码失败')
      } finally {
        this.qrLoading = false
      }
    }
  }
}
</script>

<style scoped>
.page-container {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.pagination-wrapper {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;
}
</style>